import {
  Popover,
  PopoverBody,
  PopoverContent,
  type PopoverProps,
  PopoverTrigger,
  Portal,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";
import type { TableFilter } from "@src/utils/components/filters/factories";
import { observer } from "mobx-react-lite";
import { cloneElement, type FC, type ReactElement, useRef } from "react";

type FilterPopoverProps = {
  trigger: ReactElement;
  filter: TableFilter;
  placement?: PopoverProps["placement"];
  targetId: string;
  isCollapsed?: boolean;
};

export const FilterPopover: FC<FilterPopoverProps> = observer(
  function FilterPopover({ filter, trigger, placement = "bottom-end" }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const popoverRef = useRef(null);
    const triggerRef = useRef<HTMLElement>(null);

    useOutsideClick({
      ref: popoverRef,
      handler: (event) => {
        if (triggerRef.current?.contains(event.target as Node)) return;
        onClose();
      },
    });

    return (
      <Popover
        closeOnBlur={false}
        isLazy
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        placement={placement}
      >
        <PopoverTrigger>
          {cloneElement(trigger, { ref: triggerRef })}
        </PopoverTrigger>

        <Portal>
          <PopoverContent
            ref={popoverRef}
            overflowY="auto"
            h="fit-content"
            {...filter.popoverContentProps}
            zIndex="popover"
          >
            <PopoverBody {...filter.popoverBodyProps}>
              {filter.filter}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  },
);
